import { Grid, useTheme, Box } from '@mui/material'
import { useState, useEffect } from 'react'
import { useCampaign } from '../../api/aws/useCampaign'
import { useLocation, useParams } from 'react-router-dom'
import { useCreateCampaign } from '../../api/aws/useCreateCampaign'
import EditCampaign from './EditCampaign'
import ViewMode from './ViewMode'
import RenderCampaignHeader from './RenderCampaignHeader'
import { stackStyles, StickyBox } from './style'
import DetailsLoading from '../Loading/DetailsLoading'
import { useStrategy } from '../../api/aws/useStrategy'
import useSaveCampaign from '../../api/customHooks/useSaveCampaign'
import { useQueryClient } from '@tanstack/react-query'
import { createAdSetObject, createNewCampaignObject } from './createCampaigns'
import StrategyNotesHeader from '../Strategy/StrategyNotesHeader'
import StrategyNotesSection from '../Strategy/StrategyNotesSection'
import { useUpdateItems } from '../../api/aws/useUpdateItems'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { createNoteData } from './handleFunctions'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { useEntitiesFromSearch } from 'api/search'
import { useDealData } from 'api/aws/deals/useDealData'

const RenderCampaign = () => {
  const { PK, SK, campaignId } = useParams()
  const dealId = SK?.split('DEAL_')?.[1]
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const { isEditMode, stopEdit } = useDealData(PK, SK, dealId)
  const { strategy, isLoading, isError } = useStrategy(dealId)
  const { mutate: saveCampaign } = useSaveCampaign()
  const [service, setService] = useState<any>(null)
  const { deals, organizations, products } = useEntitiesFromSearch()
  const newDeal = deals?.find(d => d?.SK === SK)
  const organization = organizations?.find(o => o?.PK === PK)
  const { campaign, isCampaignLoading, isCampaignError } = useCampaign(
    '/aws/campaigns',
    'campaigns',
    campaignId || null
  )
  const [campaignState, setCampaignState] = useState({
    ...campaign,
    items: campaign?.items || [],
  })
  const { mutate: createCampaign } = useCreateCampaign()
  //////// Notes /////////
  const { mutate: updateItems } = useUpdateItems()
  const { activeUser } = useActiveUser()
  const { showSnackbar } = useSnackbarContext()
  const [newNoteContent, setNewNoteContent] = useState('')
  const [notes, setNotes] = useState(strategy?.notes || [])
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false)
  const location = useLocation()

  useEffect(() => {
    if (!isCampaignLoading && campaign !== undefined && strategy && service && organization) {
      if (campaign.status === 204) {
        console.log('no campaign found, creating new....')
        handleCreateCampaign()
      } else {
        setCampaignState({
          name: `Campaign | ${newDeal?.name}`,
          ...campaign,
          items: campaign.items || [], // Ensure items array exists
        })
      }
    }
  }, [campaign, isCampaignLoading, strategy, service, organization])

  useEffect(() => {
    if (strategy && !isLoading) {
      setService(products?.find(product => product.instanceId === campaignId))
    }
  }, [strategy, isLoading])

  const handleCreateCampaign = () => {
    if (!strategy || !service || !organization) {
      console.warn('Dependencies missing for campaign creation:', { strategy, service, organization })
      return // Exit if dependencies are not yet loaded
    }

    setIsCreatingCampaign(true)
    const campaignData = {
      strategy: strategy,
      product: service,
      organization: organization,
    }

    try {
      createCampaign(campaignData, {
        onSuccess: data => {
          console.log('Successfully created new campaign', data)
          queryClient.invalidateQueries({ queryKey: ['/aws/campaigns', campaignId] })
          setCampaignState({ ...data.campaign })
          setIsCreatingCampaign(false)
        },
        onError: e => {
          console.error('Error creating new campaign', e)
          setIsCreatingCampaign(false)
        },
      })
    } catch (error) {
      console.error('Error creating new campaign', error)
      setIsCreatingCampaign(false)
    }
  }

  useEffect(() => {
    if (campaign) {
      console.log('campaign', campaign)
      setCampaignState({
        ...campaign,
        items: campaign.items || [], // Ensure items array exists
      })
    }
  }, [campaign])

  const handleChange = (fieldName, newValue) => {
    setCampaignState(prevState => {
      const fieldParts = fieldName.split(/[[\].]+/).filter(Boolean)
      let updatedState = { ...prevState }

      if (fieldParts.length > 1) {
        let current = updatedState
        for (let i = 0; i < fieldParts.length - 1; i++) {
          const part = fieldParts[i]
          if (!current[part]) current[part] = isNaN(fieldParts[i + 1]) ? {} : [] // Initialize if undefined
          current = current[part]
        }
        current[fieldParts[fieldParts.length - 1]] = newValue
      } else {
        updatedState[fieldName] = newValue
      }

      return updatedState
    })
  }

  const handleSave = () => {
    saveCampaign(
      { campaignId: campaignState.campaign_uuid, campaignData: campaignState },
      {
        onSuccess: () => {
          console.log('Campaign updated successfully!')
          stopEdit()
          queryClient.invalidateQueries({ queryKey: ['/aws/campaigns', campaignId] })
        },
        onError: error => {
          console.error('Failed to update campaign:', error)
          stopEdit()
        },
      }
    )
  }

  // Add a new campaign to the items array
  const handleAddCampaign = () => {
    const newCampaignData = createNewCampaignObject(campaign)
    console.log('newCampaignData', newCampaignData)
    setCampaignState(prevState => ({
      ...prevState,
      items: [...prevState.items, newCampaignData], // Add new campaign to items array
    }))
  }
  // Add a new ad set to a social campaign's ads array
  const handleCreateAdSet = itemIndex => {
    const newAdSet = createAdSetObject(campaignState, itemIndex)

    setCampaignState(prevState => {
      const updatedItems = [...prevState.items]

      // Ensure `ads` is initialized as an array if undefined
      if (!updatedItems[itemIndex].ads) {
        updatedItems[itemIndex].ads = []
      }

      updatedItems[itemIndex].ads.push(newAdSet)

      return {
        ...prevState,
        items: updatedItems,
      }
    })
  }
  /////////////////////////////Notes///////////////////////////////////
  ////////////////////////////////////////////////////////////////////
  const handleAddNote = () => {
    if (newNoteContent && newNoteContent !== '<p></p>') {
      setIsCreatingNote(true)
      // Avoid adding empty notes
      const newNote = createNoteData(newNoteContent, activeUser)

      const updatedNotes = [...notes, newNote]
      setNotes(updatedNotes)

      updateItems(
        {
          updates: [
            {
              primaryKey: 'strategy_uuid',
              tableName: 'strategy',
              itemId: newDeal.deal_uuid,
              fieldName: 'notes',
              newValue: updatedNotes,
            },
          ],
        },
        {
          onSuccess: () => {
            showSnackbar('Note added successfully', 'success')
            handleClear()
          },
          onError: err => {
            console.error('Failed to add note:', err)
            showSnackbar('Failed to add note', 'error')
          },
        }
      )
    }
  }
  const handleClear = () => {
    setIsCreatingNote(false)
    setNewNoteContent('') // This clears the note content state
  }
  ////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////

  if (!campaign) {
    return null // Early return if campaign is undefined
  }
  if (isLoading || isCampaignLoading || isCreatingCampaign || campaign.state === 204) {
    return <DetailsLoading />
  }
  return (
    <Grid container>
      {isCreatingCampaign && <DetailsLoading />}
      <Grid
        item
        xs={9}
        key={`campaigns-${campaign?.key}`}
        sx={{
          height: location?.pathname.includes('/nco') ? 'calc(100vh - 215px)' : 'calc(100vh - 120px)',
          overflowY: 'auto',
          flexGrow: 1,
          pl: 2,
          pr: 2,
          borderColor: theme.palette.text.secondary,
        }}
      >
        <StickyBox key={campaignId}>
          <RenderCampaignHeader
            key={campaignId}
            service={service}
            handleSave={handleSave}
            onAddCampaign={handleAddCampaign}
          />
        </StickyBox>

        {isEditMode ? (
          <EditCampaign campaignState={campaignState} onChange={handleChange} onCreateAdSet={handleCreateAdSet} />
        ) : (
          <ViewMode campaign={campaignState} />
        )}
      </Grid>
      <Grid item xs={3} sx={stackStyles}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            mb: 1,
          }}
        >
          <StrategyNotesHeader
            newNoteContent={newNoteContent}
            setNewNoteContent={setNewNoteContent}
            handleAddNote={handleAddNote}
            onClear={handleClear}
            isCreatingNote={isCreatingNote}
            setIsCreatingNote={setIsCreatingNote}
          />
        </Box>
        <Box>
          <StrategyNotesSection
            newNoteContent={newNoteContent}
            setNewNoteContent={setNewNoteContent}
            handleAddNote={handleAddNote}
            handleClear={handleClear}
            isCreatingNote={isCreatingNote}
            setIsCreatingNote={setIsCreatingNote}
            isEditMode={isEditMode}
            notes={notes}
            setNotes={setNotes}
          />
        </Box>
      </Grid>
    </Grid>
  )
}

export default RenderCampaign
